import { Injectable } from '@angular/core';
import Point from '@arcgis/core/geometry/Point';
import PictureMarkerSymbol from '@arcgis/core/symbols/PictureMarkerSymbol';
import * as webMercatorUtils from '@arcgis/core/geometry/support/webMercatorUtils';

/** A marker as pushed on MapService.warrantyClaimMarkers$ / warrantyIssueMarkers$. */
export interface ClusterableMarker {
  geometry: { latitude: number; longitude: number };
  data?: Record<string, unknown>;
}

export interface MarkerCluster<T extends ClusterableMarker = ClusterableMarker> {
  /** WGS84 point where the graphic is drawn (centroid of the members). */
  center: Point;
  items: T[];
  /**
   * True when every member sits on the same coordinate (to 6 decimals), i.e.
   * zooming in can never separate them. These keep the pin-with-badge look and
   * the pick-one popup; proximity clusters get a count bubble and zoom on click.
   */
  sameLocation: boolean;
  /** WGS84 bounding box of the members: [minLng, minLat, maxLng, maxLat]. */
  bbox: [number, number, number, number];
}

/**
 * Zoom-driven point clustering for GraphicsLayer-based marker layers
 * (warranty claims, punchlist items, warranty/punchlist issues).
 *
 * Those layers keep hand-drawn PictureMarkerSymbol pins and a hover/click
 * popup wired through hitTest, which does not fit the FeatureLayer
 * `featureReduction` path the picture/unified layers use. Instead the caller
 * asks this service to bucket markers by on-screen distance for the current
 * zoom and redraws the layer whenever the zoom settles.
 */
@Injectable({ providedIn: 'root' })
export class MapPointClusterService {
  /** Zoom at (or beyond) which proximity clustering is switched off. */
  static readonly MAX_CLUSTER_ZOOM = 21;
  /** Pixel radius inside which pins collapse into one cluster. Pins are 26px wide. */
  static readonly CLUSTER_RADIUS_PX = 40;

  /** Cluster radius in screen pixels for a zoom level; 0 disables proximity clustering. */
  radiusForZoom(zoom: number): number {
    if (!Number.isFinite(zoom) || zoom >= MapPointClusterService.MAX_CLUSTER_ZOOM) return 0;
    return MapPointClusterService.CLUSTER_RADIUS_PX;
  }

  /**
   * Group markers whose pins would overlap at the view's current resolution.
   * Markers on the exact same coordinate always merge, regardless of radius.
   */
  cluster<T extends ClusterableMarker>(mapView: __esri.MapView | null, markers: T[], radiusPx: number): MarkerCluster<T>[] {
    // Step 1: exact-location groups (never separable by zoom).
    const byLocation = new Map<string, T[]>();
    for (const m of markers || []) {
      const lat = Number(m?.geometry?.latitude);
      const lng = Number(m?.geometry?.longitude);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
      const key = `${lat.toFixed(6)},${lng.toFixed(6)}`;
      if (!byLocation.has(key)) byLocation.set(key, []);
      byLocation.get(key)!.push(m);
    }

    const seeds = Array.from(byLocation.values()).map(items => {
      const lat = Number(items[0].geometry.latitude);
      const lng = Number(items[0].geometry.longitude);
      const [x, y] = webMercatorUtils.lngLatToXY(lng, lat);
      return { items, lat, lng, x, y };
    });

    const resolution = Number(mapView?.resolution);
    const radiusMapUnits = radiusPx > 0 && Number.isFinite(resolution) && resolution > 0
      ? radiusPx * resolution
      : 0;

    if (radiusMapUnits <= 0) {
      return seeds.map(s => this.toCluster(s.items, true));
    }

    // Step 2: greedy proximity clustering in Web Mercator metres. Seeds are
    // processed in a stable order so the same input always yields the same
    // bubbles; the running centroid keeps bubbles centred on their members.
    interface Working { items: T[]; sumX: number; sumY: number; n: number; groups: number }
    const clusters: Working[] = [];
    for (const s of seeds) {
      let target: Working | null = null;
      for (const c of clusters) {
        const cx = c.sumX / c.n;
        const cy = c.sumY / c.n;
        const dx = cx - s.x;
        const dy = cy - s.y;
        if (Math.sqrt(dx * dx + dy * dy) <= radiusMapUnits) {
          target = c;
          break;
        }
      }
      if (target) {
        target.items.push(...s.items);
        target.sumX += s.x * s.items.length;
        target.sumY += s.y * s.items.length;
        target.n += s.items.length;
        target.groups += 1;
      } else {
        clusters.push({ items: [...s.items], sumX: s.x * s.items.length, sumY: s.y * s.items.length, n: s.items.length, groups: 1 });
      }
    }

    return clusters.map(c => this.toCluster(c.items, c.groups === 1));
  }

  private toCluster<T extends ClusterableMarker>(items: T[], sameLocation: boolean): MarkerCluster<T> {
    let minLng = Infinity, minLat = Infinity, maxLng = -Infinity, maxLat = -Infinity;
    let sumLng = 0, sumLat = 0;
    for (const m of items) {
      const lat = Number(m.geometry.latitude);
      const lng = Number(m.geometry.longitude);
      sumLat += lat; sumLng += lng;
      if (lat < minLat) minLat = lat;
      if (lat > maxLat) maxLat = lat;
      if (lng < minLng) minLng = lng;
      if (lng > maxLng) maxLng = lng;
    }
    const center = new Point({
      latitude: sumLat / items.length,
      longitude: sumLng / items.length,
      spatialReference: { wkid: 4326 },
    });
    return { center, items, sameLocation, bbox: [minLng, minLat, maxLng, maxLat] };
  }

  /**
   * Zoom level that fits a cluster's members in the view with some padding,
   * clamped to at least one level in and at most MAX_CLUSTER_ZOOM so a tight
   * cluster never sends the view past the basemap's usable range.
   */
  zoomToFit(mapView: __esri.MapView, cluster: MarkerCluster, paddingPx = 80): number {
    const currentZoom = Number(mapView.zoom) || 0;
    const [minLng, minLat, maxLng, maxLat] = cluster.bbox;
    const [x0, y0] = webMercatorUtils.lngLatToXY(minLng, minLat);
    const [x1, y1] = webMercatorUtils.lngLatToXY(maxLng, maxLat);
    const widthM = Math.abs(x1 - x0);
    const heightM = Math.abs(y1 - y0);
    const availW = Math.max(1, (mapView.width || 0) - paddingPx * 2);
    const availH = Math.max(1, (mapView.height || 0) - paddingPx * 2);
    const resolution = Number(mapView.resolution) || 1;

    let target = currentZoom + 1;
    if (widthM > 0 || heightM > 0) {
      const fitResolution = Math.max(widthM / availW, heightM / availH);
      if (fitResolution > 0) {
        target = currentZoom + Math.log2(resolution / fitResolution);
      }
    }
    target = Math.floor(target);
    target = Math.max(currentZoom + 1, target);
    return Math.min(MapPointClusterService.MAX_CLUSTER_ZOOM, target);
  }

  /**
   * Primary Blue count bubble matching the picture/unified cluster symbol
   * (#1036D6 disc, white Montserrat bold count). Diameter grows with count.
   */
  buildClusterSymbol(count: number): PictureMarkerSymbol {
    const size = Math.round(Math.min(48, 32 + Math.log2(Math.max(1, count)) * 3));
    const label = count > 999 ? '999+' : String(count);
    const fontSize = label.length > 2 ? 11 : 13;
    const r = size / 2;
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size * 4}" height="${size * 4}" viewBox="0 0 ${size} ${size}">
        <circle cx="${r}" cy="${r}" r="${r - 1.5}" fill="#1036D6" stroke="#FFFFFF" stroke-width="2"/>
        <text x="${r}" y="${r}" text-anchor="middle" dominant-baseline="central" font-family="Montserrat, Arial, sans-serif" font-size="${fontSize}" font-weight="700" fill="#FFFFFF">${label}</text>
      </svg>`;
    return new PictureMarkerSymbol({
      url: `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`,
      width: `${size}px`,
      height: `${size}px`,
    });
  }
}
